import {BaseComponent} from "../../base.component";
import {Component, OnDestroy, OnInit} from "@angular/core";
import {SessionService} from "../../common/session.service";
import {LoggerService} from "../../common/logger.service";
import {BreadcrumbService} from "../../breadcrumb/breadcrumb.service";
import {Util} from "../../util/util";
import {IProduct, IProductGroup} from "../../product/product-models";
import {ICatalogView} from "./catalog-models";
import {CartUtil} from "../cart-util";
import {CatalogService} from "./catalog.service";
import {BreadcrumbType} from "../../breadcrumb/breadcrumb.models";
import {Observable, Subscription} from "rxjs";
import {catchError, map, switchMap, tap} from "rxjs/operators";
import {AddToCartDirective} from "../add-to-cart/add-to-cart.directive";
import {ActivatedRoute} from "@angular/router";
import {UserService} from "../../common/user.service";
import {AnalyticsService} from "../../analytics/analytics.service";

@Component({
    selector: 'catalog',
    templateUrl: './catalog.component.html',
    styleUrls: ['./catalog.component.scss'],
    providers: [AddToCartDirective],
    standalone: false
})

export class CatalogComponent extends BaseComponent implements OnInit, OnDestroy {

    productGroups$: Observable<IProductGroup[]>;
    activeProductGroup: IProductGroup;
    activeHover: number;
    sortOptions: any[];
    filterView: ICatalogView;
    loadingProducts: boolean = true;
    hasProducts: boolean = true;
    filterViewSubscription: Subscription;
    showDrawer = false;
    hasHero: boolean = true;
    isCelavive: boolean = false;

    constructor(
        loggerService: LoggerService,
        private catalogService: CatalogService,
        private addToCartDirective: AddToCartDirective,
        private breadcrumbService: BreadcrumbService,
        private analyticsService: AnalyticsService,
        private route: ActivatedRoute,
        private sessionService: SessionService,
        private userService: UserService,
    ) {
        super(userService, loggerService);
    }

    ngOnInit(): void {
        this.route.paramMap.pipe(
            switchMap((params) => {
                let stateCategoryId;
                if (params.has('categoryId')) {
                    //check for value "null" that still comes in through some PWPs that needs to set category to "all" (-1)
                    stateCategoryId = params.get('categoryId') == "null" ? -1 : +params.get('categoryId');
                    this.hasHero = stateCategoryId > 0;
                } else {
                    stateCategoryId = undefined;
                }
                const stateTagId = params.has('tagId') ? +params.get('tagId') : undefined;
                return this.catalogService.setCurrentCategory(stateCategoryId, stateTagId);
            }),
            catchError(error => {
                this.log.error('CatalogComponent setting current category', error);
                return error;
            })
        ).subscribe((catalogView: ICatalogView) => this.analyticsService.catalogViewEvent(catalogView));
        this.sortOptions = this.catalogService.sortOptions;
        this.filterViewSubscription = this.catalogService.filterView$.subscribe((filterView) => {
            if (!filterView) {
                this.loadingProducts = false;
                this.hasProducts = false;
            } else if (!!filterView.selectedCategory || !!filterView.selectedTag) {
                this.loadingProducts = true;
                this.userService.getShopUser().subscribe(() => {
                    this.filterView = filterView;
                    this.setBreadcrumb();
                    this.getAllProducts();
                    this.loadProducts();
                    this.isCelavive = CartUtil.isCelaviveCategory(filterView.selectedCategory.name);
                });
            }
        });
    }

    ngOnDestroy(): void {
        if (this.filterViewSubscription) {
            this.filterViewSubscription.unsubscribe();
        }
    }

    get viewMode() {
        return this.catalogService.viewMode;
    }

    loadProducts() {
        this.productGroups$ = this.catalogService.productGroups$.pipe(
            tap((productGroups) => {
                this.loadingProducts = false;
                this.hasProducts = !!productGroups && productGroups.length > 0;
            }),
            map(productGroups => productGroups)
        );
    }

    setBreadcrumb() {
        // todo breadcrumbs for tags
        if (!!this.filterView.selectedCategory && this.filterView.selectedCategory.id > 0) {
            this.breadcrumbService.setBreadcrumb(this.filterView.selectedCategory.id, BreadcrumbType.CATEGORY);
        }
    }

    getAllProducts() {
        this.catalogService.getProductGroups().subscribe();
    }

    sideNavToggle() {
        this.catalogService.sideNavToggle();
    }

    viewModeToggle(viewMode: any) {
        this.catalogService.viewMode = viewMode;
    }

    sortCategory() {
        this.loadingProducts = true;
        this.catalogService.sortCategory(this.filterView.selectedSort);
    }

    displayUrlShareables$(productGroup: IProductGroup): Observable<boolean> {
        return this.userService.getShopUser().pipe(
            map(user => this.showUrlShareables
                && this.showCart
                && !productGroup.hasVariations
                && CartUtil.showAddToCartButtonProduct(productGroup.products[productGroup.firstProductId], user)
            )
        );
    }

    hasRetailVersion(product: IProduct): boolean {
        return !!product && !!product.retailPrice;
    }

    productSelected(productGroup: IProductGroup, element: HTMLButtonElement) {
        this.activeProductGroup = productGroup;
        this.sessionService.isRetailUser().then(isRetail => {
            if (isRetail || !Util.isMobile()) {
                this.addProductToCart(productGroup, element);
            } else {
                this.openAddToCartDrawer();
            }
        });
    }

    addProductToCart(productGroup: IProductGroup, btnElement: HTMLButtonElement) {
        this.addToCartDirective.quantity = 1;
        this.addToCartDirective.product = productGroup.products[productGroup.firstProductId];
        this.addToCartDirective.buttonElement = btnElement;
        this.addToCartDirective.addToCart();
    }

    openAddToCartDrawer() {
        this.showDrawer = true;
    }

    showAddToCartButton$(productGroup: IProductGroup): Observable<boolean> {
        return this.userService.getShopUser().pipe(
            map(user => CartUtil.showAddToCartButtonProductGroup(productGroup, user))
        );
    }
}
